import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import { Box, IconButton, Tooltip } from '@mui/material'
import { alpha, darken } from '@mui/material/styles'
import { useContext } from 'react'
import { InTableContext } from './TableContext'

const adjustColor = (color, amount) => {
  return color === 'rgb(33, 43, 54)' ? alpha(color, 0.8) : darken(color, 0.1)
}

const colorScheme = {
  black: {
    backgroundColor: 'rgb(33, 43, 54)',
    color: 'white',
    disabled: {
      backgroundColor: 'rgba(33, 43, 54, 0.2)',
      color: 'white'
    }
  },

  blackOutlined: {
    backgroundColor: 'transparent',
    color: 'rgb(33, 43, 54)',
    border: '1px solid rgba(33, 43, 54, 0.5)',
    hoverBgColor: 'rgba(33, 43, 54, 0.08)',
    disabled: {
      backgroundColor: 'transparent',
      color: 'rgba(33, 43, 54, 0.4)',
      border: '1px solid rgba(33, 43, 54, 0.2)'
    }
  },

  neutral: {
    backgroundColor: 'rgb(244, 246, 248)',
    color: 'rgb(33, 43, 54)',
    hoverBgColor: 'rgb(237, 239, 241)',
    disabled: {
      backgroundColor: 'rgba(244, 246, 248, 0.5)',
      color: 'rgba(33, 43, 54, 0.4)'
    }
  },

  neutralOutlined: {
    backgroundColor: 'transparent',
    color: 'rgb(33, 43, 54)',
    border: '1px solid rgba(33, 43, 54, 0.3)',
    hoverBgColor: 'rgba(33, 43, 54, 0.06)',
    disabled: {
      backgroundColor: 'transparent',
      color: 'rgba(33, 43, 54, 0.4)',
      border: '1px solid rgba(33, 43, 54, 0.15)'
    }
  },

  lRed: {
    backgroundColor: 'rgb(255, 230, 225)',
    color: 'rgb(183, 29, 24)',
    hoverBgColor: '#ffccc3',
    disabled: {
      backgroundColor: 'rgba(255, 230, 225, 0.5)',
      color: 'rgba(183, 29, 24, 0.5)'
    }
  },

  lRedOutlined: {
    backgroundColor: 'transparent',
    color: 'rgb(183, 29, 24)',
    border: '1px solid rgba(183, 29, 24, 0.4)',
    hoverBgColor: 'rgba(183, 29, 24, 0.08)',
    disabled: {
      backgroundColor: 'transparent',
      color: 'rgba(183, 29, 24, 0.4)',
      border: '1px solid rgba(183, 29, 24, 0.2)'
    }
  },

  red: {
    backgroundColor: 'rgb(211, 47, 47)',
    color: 'white',
    hoverBgColor: 'rgb(183, 28, 28)',
    disabled: {
      backgroundColor: 'rgba(211, 47, 47, 0.5)',
      color: 'white'
    }
  },

  redOutlined: {
    backgroundColor: 'transparent',
    color: 'rgb(211, 47, 47)',
    border: '1px solid rgba(211, 47, 47, 0.5)',
    hoverBgColor: 'rgba(211, 47, 47, 0.08)',
    disabled: {
      backgroundColor: 'transparent',
      color: 'rgba(211, 47, 47, 0.4)',
      border: '1px solid rgba(211, 47, 47, 0.25)'
    }
  },

  lGreen: {
    backgroundColor: 'rgb(230, 249, 241)',
    color: 'rgb(0, 133, 88)',
    hoverBgColor: 'rgb(204, 241, 228)',
    disabled: {
      backgroundColor: 'rgba(230, 249, 241, 0.5)',
      color: 'rgba(0, 133, 88, 0.5)'
    }
  },

  lGreenOutlined: {
    backgroundColor: 'transparent',
    color: 'rgb(0, 133, 88)',
    border: '1px solid rgba(0, 133, 88, 0.4)',
    hoverBgColor: 'rgba(0, 133, 88, 0.08)',
    disabled: {
      backgroundColor: 'transparent',
      color: 'rgba(0, 133, 88, 0.4)',
      border: '1px solid rgba(0, 133, 88, 0.2)'
    }
  },

  green: {
    backgroundColor: 'rgba(0, 167, 111, 1)',
    color: 'white',
    hoverBgColor: 'rgba(0, 133, 88, 1)',
    disabled: {
      backgroundColor: 'rgba(0, 167, 111, 0.5)',
      color: 'white'
    }
  },

  greenOutlined: {
    backgroundColor: 'transparent',
    color: 'rgba(0, 167, 111, 1)',
    border: '1px solid rgba(0, 167, 111, 0.5)',
    hoverBgColor: 'rgba(0, 167, 111, 0.08)',
    disabled: {
      backgroundColor: 'transparent',
      color: 'rgba(0, 167, 111, 0.4)',
      border: '1px solid rgba(0, 167, 111, 0.25)'
    }
  },

  lBlue: {
    backgroundColor: 'rgb(232, 244, 253)',
    color: 'rgb(0, 82, 204)',
    hoverBgColor: 'rgb(210, 232, 252)',
    disabled: {
      backgroundColor: 'rgba(232, 244, 253, 0.5)',
      color: 'rgba(0, 82, 204, 0.5)'
    }
  },

  lBlueOutlined: {
    backgroundColor: 'transparent',
    color: 'rgb(0, 82, 204)',
    border: '1px solid rgba(0, 82, 204, 0.4)',
    hoverBgColor: 'rgba(0, 82, 204, 0.08)',
    disabled: {
      backgroundColor: 'transparent',
      color: 'rgba(0, 82, 204, 0.4)',
      border: '1px solid rgba(0, 82, 204, 0.2)'
    }
  },

  blue: {
    backgroundColor: 'rgba(25, 118, 210, 1)',
    color: 'white',
    hoverBgColor: 'rgba(0, 82, 204, 1)',
    disabled: {
      backgroundColor: 'rgba(25, 118, 210, 0.5)',
      color: 'white'
    }
  },

  blueOutlined: {
    backgroundColor: 'transparent',
    color: 'rgba(25, 118, 210, 1)',
    border: '1px solid rgba(25, 118, 210, 0.5)',
    hoverBgColor: 'rgba(25, 118, 210, 0.08)',
    disabled: {
      backgroundColor: 'transparent',
      color: 'rgba(25, 118, 210, 0.4)',
      border: '1px solid rgba(25, 118, 210, 0.25)'
    }
  },

  warning: {
    backgroundColor: 'rgb(255, 171, 0)',
    color: 'white',
    hoverBgColor: 'rgba(230, 156, 0, 1)',
    disabled: {
      backgroundColor: 'rgba(255, 171, 0, 0.5)',
      color: 'white'
    }
  },

  warningOutlined: {
    backgroundColor: 'transparent',
    color: 'rgb(255, 171, 0)',
    border: '1px solid rgba(255, 171, 0, 0.6)',
    hoverBgColor: 'rgba(255, 171, 0, 0.12)',
    disabled: {
      backgroundColor: 'transparent',
      color: 'rgba(255, 171, 0, 0.4)',
      border: '1px solid rgba(255, 171, 0, 0.25)'
    }
  }
}

const typeToIcon = {
  create: AddIcon,
  edit: EditIcon,
  delete: DeleteIcon
}

const typeToColor = {
  create: 'black'
}

const CustomButton = ({
  type,
  buttonText,
  onClick,
  sx,
  disabled,
  color,
  sxButton,
  icon: IconComponent
}) => {
  const inTable = useContext(InTableContext)
  const DefaultIcon = type ? typeToIcon[type] : null
  const Icon = IconComponent || DefaultIcon

  // Row actions inside tables: icon with the label as tooltip
  if (inTable && buttonText && Icon) {
    const isDestructive =
      type === 'delete' || /^(delete|remove|kick)/i.test(String(buttonText))
    return (
      <Tooltip title={buttonText}>
        {/* span keeps the tooltip working on disabled buttons */}
        <span>
          <IconButton
            size="small"
            onClick={(e) => onClick(e)}
            disabled={disabled}
            aria-label={String(buttonText)}
            sx={{
              color: 'text.secondary',
              borderRadius: '8px',
              '&:hover': {
                color: isDestructive ? 'error.main' : 'text.primary',
                backgroundColor: isDestructive
                  ? 'rgba(255, 86, 48, 0.08)'
                  : 'rgba(145, 158, 171, 0.12)'
              }
            }}
          >
            <Icon sx={{ fontSize: 20 }} />
          </IconButton>
        </span>
      </Tooltip>
    )
  }

  const defaultColorScheme = type
    ? colorScheme[typeToColor[type]]
    : colorScheme[color]

  const hoverBgColor = defaultColorScheme?.hoverBgColor
    ? defaultColorScheme.hoverBgColor
    : adjustColor(
        sx?.backgroundColor ??
          defaultColorScheme?.backgroundColor ??
          'rgba(145, 158, 171, 0.16)'
      )

  return (
    <Box>
      <IconButton
        disableRipple
        onClick={(e) => onClick(e)}
        variant="contained"
        size="small"
        sx={{
          ...defaultColorScheme,
          borderRadius: '8px',
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '0.875rem',
          lineHeight: 1.5,
          py: '8px',
          px: buttonText ? '14px' : '8px',
          gap: buttonText ? '6px' : 0,
          whiteSpace: 'nowrap',
          '&:hover': {
            backgroundColor: hoverBgColor,
            boxShadow: 'none'
          },
          '&:disabled': defaultColorScheme?.disabled,
          ...sx
        }}
        disabled={disabled}
      >
        {IconComponent ? (
          <IconComponent sx={{ fontSize: 20, ...sxButton }} />
        ) : DefaultIcon ? (
          <DefaultIcon sx={{ fontSize: 20, ...sxButton }} />
        ) : null}
        {buttonText}
      </IconButton>
    </Box>
  )
}
export default CustomButton
